import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

// Two-stage timeout. The first stage swaps the spinner copy for a
// "taking longer than expected" message so the user knows something is
// still happening on slow mobile connections; the second stage gives
// up and routes back to login. supabase-js parsing the URL hash and
// hydrating the session can take >3s on poor connections — 8s is a
// safer ceiling.
const SESSION_DETECT_DELAYED_MS = 3000;
const SESSION_DETECT_TIMEOUT_MS = 8000;
// Mirror the backend Fastify schema constraints on `orgName`.
const ORG_NAME_MIN_LENGTH = 1;
const ORG_NAME_MAX_LENGTH = 255;

const apiBaseUrl = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export function SignupFinalize() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [delayed, setDelayed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const delayedTimer = setTimeout(() => {
      if (!cancelled) setDelayed(true);
    }, SESSION_DETECT_DELAYED_MS);
    const timeout = setTimeout(() => {
      if (!cancelled) navigate("/login", { replace: true });
    }, SESSION_DETECT_TIMEOUT_MS);

    void (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      clearTimeout(delayedTimer);
      clearTimeout(timeout);

      if (!session) {
        navigate("/login", { replace: true });
        return;
      }

      const meta = session.user.user_metadata as {
        pending_org_name?: string;
        pending_tenant_id?: string;
      };

      // Refresh after saga (or admin-bootstrap path) — metadata already
      // rewritten, just hand off to /complete-setup.
      if (meta.pending_tenant_id) {
        navigate("/complete-setup", { replace: true });
        return;
      }

      // Stale-link reuse: no pending_org_name and no pending_tenant_id.
      // If the session already carries a tenant in app_metadata, the
      // user is fully linked — go to dashboard. Otherwise login.
      if (!meta.pending_org_name) {
        const appMeta = session.user.app_metadata as
          | { tenant_id?: string }
          | undefined;
        navigate(appMeta?.tenant_id ? "/" : "/login", { replace: true });
        return;
      }

      // Happy path — call POST /v1/signup. The Signup form caps orgName
      // client-side, but a caller that hit `signInWithOtp` directly can
      // store an arbitrarily long `pending_org_name`. Bounce that
      // locally with a clear message rather than relying on a generic
      // 400 from the backend schema.
      const pendingOrgName = meta.pending_org_name;
      if (!pendingOrgName || pendingOrgName.length > ORG_NAME_MAX_LENGTH) {
        setError(
          `Organisation name must be ${String(ORG_NAME_MIN_LENGTH)}–${String(ORG_NAME_MAX_LENGTH)} characters`,
        );
        return;
      }
      try {
        const res = await fetch(`${apiBaseUrl}/v1/signup`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ orgName: pendingOrgName }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            detail?: string;
          } | null;
          setError(body?.detail ?? `Signup failed (${String(res.status)})`);
          return;
        }
        const body = (await res.json()) as {
          tenant: { id: string; orgName: string };
          alreadyLinked?: boolean;
        };
        if (body.alreadyLinked) {
          await supabase.auth.refreshSession();
          navigate("/", { replace: true });
        } else {
          // Metadata is now `pending_tenant_id`. Refresh the session so
          // the SDK reads the new user_metadata, then /complete-setup
          // takes over.
          await supabase.auth.refreshSession();
          navigate("/complete-setup", { replace: true });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Network error");
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(delayedTimer);
      clearTimeout(timeout);
    };
  }, [navigate]);

  if (error) {
    return (
      <AuthLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Signup failed</CardTitle>
            <CardDescription>
              We couldn&apos;t finish setting up your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full"
              onClick={() => {
                navigate("/signup", { replace: true });
              }}
            >
              Try again
            </Button>
          </CardFooter>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center gap-4 p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <p className="text-center text-muted-foreground">
            {delayed
              ? "Taking longer than expected — hang on…"
              : "Setting up your workspace…"}
          </p>
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
